import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { api, assetUrl } from "@/lib/api";
import { ENTRY, FLOOR, PHONE, TEL, useCafe } from "@/lib/cafe";
import { track } from "@/lib/track";

const HOLD = 8000;
const FADE = 2500;

const useCycle = (n) => {
  const [i, setI] = useState(0);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (n < 2) return undefined;
    const t = setTimeout(() => setI((k) => (k + 1) % n), HOLD + FADE);
    return () => clearTimeout(t);
  }, [n, i, tick]);
  const go = (k) => { setI(((k % n) + n) % n); setTick((t) => t + 1); };
  return [i, go];
};

// Real whole-plate originals (Explore card photos), editorial CSS grade + vignette inside a coral-ringed circle. Never cropped, zoomed or AI.
const PlateCircle = ({ items, i, go }) => {
  const start = useRef(null);
  return (
    <div data-testid="showcase-circle" className="relative aspect-square w-[min(680px,calc(100vw-80px))] shrink-0 landscape:md:w-[min(600px,78vh)] touch-pan-y select-none rounded-full bg-[#F6F2EC] ring-1 ring-[#B8326C] ring-offset-[6px] ring-offset-white"
      onPointerDown={(e) => { start.current = e.clientX; }}
      onPointerUp={(e) => { const dx = e.clientX - (start.current ?? e.clientX); if (Math.abs(dx) > 40) { go(i + (dx < 0 ? 1 : -1)); track("home_showcase_swiped"); } start.current = null; }}>
      <div className="absolute inset-0 overflow-hidden rounded-full">
        {/* Only the current, previous (fading out) and next (preloading) plates are mounted, so first load fetches 2 photos, not all. */}
        {items.map((it, n) => ([i, (i + 1) % items.length, (i - 1 + items.length) % items.length].includes(n) || null) && (
          <img key={it.url} src={assetUrl(it.url)} alt={n === i ? it.name : ""} draggable={false} data-testid={n === i ? "hero-dish-active" : undefined} data-dish={it.dish_id}
            decoding="async" fetchpriority={n === 0 ? "high" : "low"} loading={n === i ? "eager" : "lazy"}
            style={{ opacity: n === i ? 1 : 0, transition: `opacity ${FADE}ms ease-in-out`, filter: "saturate(1.08) contrast(1.05) brightness(1.02) sepia(0.05)" }}
            className="absolute inset-0 h-full w-full object-contain" />
        ))}
        <span aria-hidden className="absolute inset-0 rounded-full" style={{ background: "radial-gradient(55% 45% at 36% 30%, rgba(255,222,186,0.22), rgba(255,222,186,0) 70%), radial-gradient(circle, rgba(11,46,68,0) 60%, rgba(11,46,68,0.24) 100%)" }} />
      </div>
    </div>
  );
};

const Dots = ({ items, i, go }) => (
  <div data-testid="showcase-dots" className="mt-3 flex flex-wrap justify-center">
    {items.map((it, n) => (
      <button key={it.url} type="button" aria-label={`Show ${it.name}`} aria-current={n === i} data-testid={`showcase-dot-${n}`} onClick={() => go(n)}
        className="flex h-12 w-9 items-center justify-center">
        <span className={`block h-2 rounded-full transition-[width,background-color] duration-500 ${n === i ? "w-6 bg-[#B8326C]" : "w-2 bg-[#0B2E44]/35"}`} />
      </button>
    ))}
  </div>
);

export const HomeShowcase = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/home-dishes").then((r) => setItems(r.data.items)).catch(() => {}); }, []);
  const [i, go] = useCycle(items.length);
  const it = items[i];
  return (
    <section id="from-our-kitchen" data-testid="home-showcase" className="relative z-10 mx-auto max-w-[1400px] px-4 pb-10 pt-4 sm:px-6">
      <div data-testid="showcase-frame" className="flex flex-col items-center gap-6 rounded-[40px] bg-white/90 p-5 text-center shadow-[0_40px_100px_-60px_rgba(11,46,68,0.5)] sm:p-8">
        <div className="flex max-w-2xl flex-col items-center gap-3">
          <p data-testid="showcase-eyebrow" className="t-eyebrow">From our kitchen</p>
          <h2 data-testid="showcase-heading" className="font-display text-[40px] leading-[1.05] text-[#0B2E44]">Fine dining, composed for your biology</h2>
          <p className="t-oneliner">Chef-plated, seasonal, every dish designed with our science team.</p>
        </div>
        <div className="flex shrink-0 flex-col items-center">
          {items.length > 0 && <PlateCircle items={items} i={i} go={go} />}
          {items.length > 1 && <Dots items={items} i={i} go={go} />}
        </div>
        {it && (
          <div data-testid="showcase-dish" className="max-w-2xl">
            <p data-testid="showcase-dish-name" className="font-title text-[21px] text-[#0B2E44]">{it.name}</p>
            {it.one_liner && <p data-testid="showcase-dish-line" className="t-oneliner mt-1 line-clamp-3">{it.one_liner}</p>}
          </div>
        )}
      </div>
    </section>
  );
};

export const ScrollCue = () => (
  <a href="#from-our-kitchen" data-testid="welcome-scroll-cue" aria-label="See more below" onClick={() => track("home_scroll_cue")}
    className="mx-auto flex h-14 w-14 flex-col items-center justify-center gap-1 text-[#0B2E44]">
    <span aria-hidden className="scroll-cue-line relative block h-7 w-px overflow-hidden bg-[#0B2E44]/25"><span className="absolute inset-x-0 top-0 h-3 bg-[#B8326C]" /></span>
    <svg aria-hidden viewBox="0 0 24 24" className="scroll-cue-chevron h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M6 9l6 6 6-6" /></svg>
  </a>
);

const RESERVE_CLS = "z6-btn z6-btn--pink min-h-[52px] bg-white/60";

// Always visible. Opens the staff booking link in a new tab, or a "reservations open soon" sheet when none is set.
export const ReserveButton = () => {
  const c = useCafe();
  const [open, setOpen] = useState(false);
  const label = <>Reserve <em>now</em><span aria-hidden className="z6-arrow" /></>;
  if (c.booking_url) {
    return <a href={c.booking_url} target="_blank" rel="noreferrer" data-testid="welcome-reserve-btn" onClick={() => track("reserve_clicked")} className={RESERVE_CLS}>{label}</a>;
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button type="button" data-testid="welcome-reserve-btn" onClick={() => { track("reserve_clicked"); setOpen(true); }} className={RESERVE_CLS}>{label}</button>
      <DialogContent data-testid="reserve-sheet" className="max-w-[460px] rounded-[28px] border-0 bg-white p-8 text-[#0B2E44]">
        <p className="t-eyebrow">Reservations</p>
        <DialogTitle className="font-display text-[30px] font-normal leading-tight text-[#0B2E44]">Reserve a table</DialogTitle>
        <DialogDescription className="font-ui text-[17px] leading-relaxed text-[#33566B]">Call our team to enquire about a table.</DialogDescription>
        <a href={TEL} data-testid="reserve-sheet-call" onClick={() => track("call_clicked")} className="z6-btn z6-btn--pink w-fit">Call <em>{PHONE}</em></a>
        <p data-testid="reserve-sheet-floor" className="font-ui text-[16px] text-[#0B2E44]">{FLOOR}</p>
        {c.address && <p data-testid="reserve-sheet-address" className="font-ui text-[16px] leading-snug text-[#0B2E44]">{c.address}</p>}
        <p data-testid="reserve-sheet-entry" className="font-ui text-[15px] leading-snug text-[#33566B]">{ENTRY}</p>
        {c.maps_url && (
          <a href={c.maps_url} target="_blank" rel="noreferrer" data-testid="reserve-sheet-directions" onClick={() => track("directions_clicked")} className="z6-btn w-fit">
            <MapPin className="h-4 w-4" strokeWidth={1.6} />Directions
          </a>
        )}
      </DialogContent>
    </Dialog>
  );
};
